import config from 'src/config';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';
import { executeEngineQuery } from 'src/app/portfolio/state/api/engineQueryUtils';
import { siteNatureRiskOverviewQuery, type SiteNatureRiskOverviewResult } from './siteNatureRiskOverviewQuery';
import { siteStateOfNatureQuery, type SiteStateOfNatureResult } from './siteStateOfNatureQuery';
import { siteThreatenedSpeciesQuery, type SiteThreatenedSpeciesResult } from './siteThreatenedSpeciesQuery';
import { siteProtectedAreasQuery, type SiteProtectedAreasResult, type OverlapType } from './siteProtectedAreasQuery';
import { siteRiskInsightsAvailableDatesQuery, type SiteRiskInsightsAvailableDatesResult } from './siteRiskInsightsAvailableDatesQuery';

export type SiteNatureRiskOverviewQueryArgs = {
    organisationBusinessCentralId: string;
    siteId: string;
    at?: string;
};

export type SiteThreatenedSpeciesQueryArgs = SiteNatureRiskOverviewQueryArgs & {
    page: number;
    pageSize: number;
};

export type SiteProtectedAreasQueryArgs = SiteNatureRiskOverviewQueryArgs & {
    page: number;
    pageSize: number;
    search?: string;
    sortDirection?: 'ASC' | 'DESC';
    /** Omitted, every area within the reporting distance is returned. */
    overlapTypes?: OverlapType[];
};

const siteNatureRiskEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        getSiteNatureRiskOverview: builder.query<SiteNatureRiskOverviewResult, SiteNatureRiskOverviewQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteNatureRiskOverviewResult>(
                    `${config.engineApiUrl.replace(/\/$/, '')}/query`,
                    siteNatureRiskOverviewQuery,
                    {
                        organisationBusinessCentralId: arg.organisationBusinessCentralId,
                        siteId: arg.siteId,
                        at: arg.at ?? null,
                    }
                ),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        getSiteRiskInsightsAvailableDates: builder.query<SiteRiskInsightsAvailableDatesResult, SiteNatureRiskOverviewQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteRiskInsightsAvailableDatesResult>(
                    `${config.engineApiUrl.replace(/\/$/, '')}/query`,
                    siteRiskInsightsAvailableDatesQuery,
                    {
                        organisationBusinessCentralId: arg.organisationBusinessCentralId,
                        siteId: arg.siteId,
                    }
                ),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        getSiteStateOfNature: builder.query<SiteStateOfNatureResult, SiteNatureRiskOverviewQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteStateOfNatureResult>(`${config.engineApiUrl.replace(/\/$/, '')}/query`, siteStateOfNatureQuery, {
                    organisationBusinessCentralId: arg.organisationBusinessCentralId,
                    siteId: arg.siteId,
                    at: arg.at ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        getSiteThreatenedSpecies: builder.query<SiteThreatenedSpeciesResult, SiteThreatenedSpeciesQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteThreatenedSpeciesResult>(
                    `${config.engineApiUrl.replace(/\/$/, '')}/query`,
                    siteThreatenedSpeciesQuery,
                    {
                        organisationBusinessCentralId: arg.organisationBusinessCentralId,
                        siteId: arg.siteId,
                        page: arg.page,
                        pageSize: arg.pageSize,
                    }
                ),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        getSiteProtectedAreas: builder.query<SiteProtectedAreasResult, SiteProtectedAreasQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteProtectedAreasResult>(`${config.engineApiUrl.replace(/\/$/, '')}/query`, siteProtectedAreasQuery, {
                    organisationBusinessCentralId: arg.organisationBusinessCentralId,
                    siteId: arg.siteId,
                    at: arg.at ?? null,
                    page: arg.page,
                    pageSize: arg.pageSize,
                    search: arg.search ?? null,
                    sortDirection: arg.sortDirection ?? null,
                    overlapTypes: arg.overlapTypes ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
    }),
});

export const {
    useGetSiteNatureRiskOverviewQuery,
    useGetSiteStateOfNatureQuery,
    useGetSiteThreatenedSpeciesQuery,
    useGetSiteProtectedAreasQuery,
    useGetSiteRiskInsightsAvailableDatesQuery,
} = siteNatureRiskEngineSlice;

export default siteNatureRiskEngineSlice;
